// Clientes Page
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { clientesAPI, personasContactoAPI, presupuestosAPI, sectoresAPI, interaccionesAPI, api } from '../api/api';
import FaceDestinos from '../components/FaceDestinos';
import { fechaLocalISO, parsearFechaServidor } from '../utils/pantallasUnDia';
import DireccionAutocomplete from '../components/DireccionAutocomplete';
import { Plus, Search, Edit, Trash2, Mail, Phone, User, TrendingUp, Calendar, Users, DollarSign, Clock, BarChart3, X, MapPin, Building, FileText, ExternalLink, FilePlus, Archive, RotateCcw, Tag, Settings2, Star, MessageSquare, PhoneCall, Coffee, Send, StickyNote, MessageCircle, AlertTriangle, Download } from 'lucide-react';

// Normaliza un teléfono a formato internacional sin signos (+34XXXXXXXXX → 34XXXXXXXXX)
// para construir URLs wa.me/. Si no se reconoce prefijo, asume España.
const telefonoParaWhatsapp = (telefono) => {
  if (!telefono) return null;
  const limpio = String(telefono).replace(/[^0-9+]/g, '');
  if (!limpio) return null;
  if (limpio.startsWith('+')) return limpio.slice(1);
  if (limpio.startsWith('00')) return limpio.slice(2);
  if (limpio.startsWith('34')) return limpio;
  // 9 dígitos = móvil/fijo español sin prefijo
  if (limpio.length === 9) return `34${limpio}`;
  return limpio;
};

const construirMailto = (email, asunto = '', cuerpo = '') => {
  if (!email) return null;
  const params = [];
  if (asunto) params.push(`subject=${encodeURIComponent(asunto)}`);
  if (cuerpo) params.push(`body=${encodeURIComponent(cuerpo)}`);
  return `mailto:${email}${params.length ? `?${params.join('&')}` : ''}`;
};

const construirWhatsapp = (telefono, mensaje = '') => {
  const numero = telefonoParaWhatsapp(telefono);
  if (!numero) return null;
  return `https://wa.me/${numero}${mensaje ? `?text=${encodeURIComponent(mensaje)}` : ''}`;
};

// Descarga el PDF de una factura usando axios (mantiene el header de Authorization)
// y dispara la descarga del blob en el navegador.
const descargarFacturaPdf = async (facturaId, numeroFactura) => {
  try {
    const response = await api.get(`/facturas/${facturaId}/pdf`, { responseType: 'blob' });
    const blob = new Blob([response.data], { type: response.headers['content-type'] || 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `factura_${numeroFactura || facturaId}.pdf`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (error) {
    alert('No se pudo descargar la factura: ' + (error.response?.data?.detail || error.message));
  }
};

const Clientes = () => {
  const navigate = useNavigate();
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterEstado, setFilterEstado] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingCliente, setEditingCliente] = useState(null);
  const [formData, setFormData] = useState({
    nombre_comercial: '',
    nombre_fiscal: '',
    cif: '',
    direccion: '',
    direccion_adicional: '',
    email: '',
    telefono: '',
    estado: 'activo',
    sector_id: '',
    tags: [],
    es_vip: false,
    // Administración pública (FACe)
    es_administracion_publica: false,
    // Contacto principal
    contacto_nombre: '',
    contacto_cargo: '',
    contacto_email: '',
    contacto_telefono: '',
    contacto_notas: ''
  });

  // Estados para modal de detalles
  const [showDetallesModal, setShowDetallesModal] = useState(false);
  const [selectedCliente, setSelectedCliente] = useState(null);
  const [clienteEstadisticas, setClienteEstadisticas] = useState(null);
  const [clienteEstadisticasPago, setClienteEstadisticasPago] = useState(null);
  const [clientePersonasContacto, setClientePersonasContacto] = useState([]);
  const [clientePresupuestos, setClientePresupuestos] = useState([]);
  const [clienteInteracciones, setClienteInteracciones] = useState([]);
  const [clienteFacturasPendientes, setClienteFacturasPendientes] = useState(null);
  const [loadingDetalles, setLoadingDetalles] = useState(false);

  // Estados para editar contacto principal
  const [editandoContactoPrincipal, setEditandoContactoPrincipal] = useState(false);
  const [contactoPrincipalData, setContactoPrincipalData] = useState({
    persona_contacto: '',
    cargo: '',
    email: '',
    telefono: '',
    extension: ''
  });

  // Estados para editar contacto adicional
  const [editandoContactoAdicional, setEditandoContactoAdicional] = useState(null);
  const [contactoAdicionalData, setContactoAdicionalData] = useState({
    nombre: '',
    cargo: '',
    email: '',
    telefono: '',
    extension: '',
    notas: ''
  });
  
  // Estados para editar datos de la empresa
  const [editandoEmpresa, setEditandoEmpresa] = useState(false);
  const [empresaData, setEmpresaData] = useState({
    nombre_empresa: '',
    nombre_fiscal: '',
    cif: '',
    direccion: '',
    sector_id: '',
    tags: [],
    es_vip: false
  });
  
  // Estados para añadir nuevo contacto
  const [showAddContactoModal, setShowAddContactoModal] = useState(false);
  const [nuevoContactoData, setNuevoContactoData] = useState({
    nombre: '',
    cargo: '',
    email: '',
    telefono: '',
    extension: '',
    notas: ''
  });
  
  // Estados para paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [totalClientes, setTotalClientes] = useState(0);
  const [mostrarArchivados, setMostrarArchivados] = useState(false);
  const [sectores, setSectores] = useState([]);
  const [filterSector, setFilterSector] = useState('');
  const [showSectoresModal, setShowSectoresModal] = useState(false);
  const [tagsDisponibles, setTagsDisponibles] = useState([]);
  const [filterTag, setFilterTag] = useState('');
  const [soloVip, setSoloVip] = useState(false);

  useEffect(() => {
    fetchClientes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mostrarArchivados]);

  useEffect(() => {
    fetchSectores();
    fetchTags();
  }, []);

  const fetchSectores = async () => {
    try {
      const res = await sectoresAPI.getAll();
      setSectores(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error('Error fetching sectores:', error);
    }
  };

  const fetchTags = async () => {
    try {
      const res = await clientesAPI.getTags();
      setTagsDisponibles(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error('Error fetching tags:', error);
    }
  };

  const recargarInteracciones = async (clienteId) => {
    try {
      const res = await interaccionesAPI.list(clienteId);
      setClienteInteracciones(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error('Error recargando interacciones:', error);
    }
  };

  const handleCrearInteraccion = async (payload) => {
    if (!selectedCliente) return;
    try {
      await interaccionesAPI.create(selectedCliente.id, payload);
      await recargarInteracciones(selectedCliente.id);
    } catch (error) {
      alert(error?.response?.data?.detail || 'Error añadiendo interacción');
    }
  };

  const handleActualizarInteraccion = async (id, payload) => {
    try {
      await interaccionesAPI.update(id, payload);
      if (selectedCliente) await recargarInteracciones(selectedCliente.id);
    } catch (error) {
      alert(error?.response?.data?.detail || 'Error actualizando interacción');
    }
  };

  const handleBorrarInteraccion = async (id) => {
    if (!window.confirm('¿Eliminar esta interacción?')) return;
    try {
      await interaccionesAPI.delete(id);
      if (selectedCliente) await recargarInteracciones(selectedCliente.id);
    } catch (error) {
      alert(error?.response?.data?.detail || 'Error eliminando interacción');
    }
  };

  const handleCrearSector = async (nombre) => {
    const n = (nombre || '').trim();
    if (!n) return;
    try {
      await sectoresAPI.create({ nombre: n });
      await fetchSectores();
    } catch (error) {
      alert(error?.response?.data?.detail || 'Error creando sector');
    }
  };

  const handleEditarSector = async (id, nombre) => {
    const n = (nombre || '').trim();
    if (!n) return;
    try {
      await sectoresAPI.update(id, { nombre: n });
      await fetchSectores();
    } catch (error) {
      alert(error?.response?.data?.detail || 'Error editando sector');
    }
  };

  const handleBorrarSector = async (id, nombre) => {
    if (!window.confirm(`¿Borrar el sector "${nombre}"? Solo funciona si no hay clientes asignados.`)) return;
    try {
      await sectoresAPI.delete(id);
      await fetchSectores();
      if (filterSector === id) setFilterSector('');
    } catch (error) {
      alert(error?.response?.data?.detail || 'Error borrando sector');
    }
  };

  useEffect(() => {
    // Abrir cliente automáticamente si viene en la URL (tras cargar la lista)
    const params = new URLSearchParams(window.location.search);
    const clienteId = params.get('cliente_id');
    if (clienteId && clientes.length > 0) {
      const cliente = clientes.find(c => c.id === clienteId);
      if (cliente) {
        openDetallesModal(cliente);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientes.length]);

  const fetchClientes = async () => {
    try {
      setLoading(true);
      
      // Obtener solo clientes primero (sin presupuestos para carga rápida inicial)
      const [clientesResponse, actividadResponse] = await Promise.all([
        clientesAPI.getAll(null, { incluir_archivados: mostrarArchivados }),
        clientesAPI.getActividadResumen().catch(() => ({ data: {} }))
      ]);

      const clientesData = clientesResponse.data;
      let clientesList = Array.isArray(clientesData)
        ? clientesData
        : (Array.isArray(clientesData?.clientes) ? clientesData.clientes : []);
      const actividadMap = actividadResponse.data || {};

      // Ordenar por fecha de alta (más recientes primero)
      clientesList.sort((a, b) => {
        const fechaA = a.fecha_alta ? new Date(a.fecha_alta) : new Date(0);
        const fechaB = b.fecha_alta ? new Date(b.fecha_alta) : new Date(0);
        return fechaB - fechaA;
      });

      // Estado dinámico en base a días desde último evento confirmado/facturado
      const clasificarEstado = (dias) => {
        if (dias === null || dias === undefined) return 'nuevo';
        if (dias < 30) return 'activo';
        if (dias <= 90) return 'reciente';
        if (dias <= 180) return 'inactivo';
        return 'dormido';
      };

      clientesList = clientesList.map(cliente => {
        const info = actividadMap[cliente.id];
        const diasPasado = info?.dias_desde_ultimo ?? null;
        const diasFuturo = info?.dias_hasta_proximo ?? null;
        // Si no hay pasado pero sí futuro, el cliente está activo porque tiene un evento programado
        const diasParaClasificar = diasPasado != null ? diasPasado : (diasFuturo != null ? 0 : null);
        return {
          ...cliente,
          estado_dinamico: clasificarEstado(diasParaClasificar),
          dias_desde_ultimo: diasPasado,
          dias_hasta_proximo: diasFuturo,
          ultimo_evento_fecha: info?.ultimo_evento_fecha || null,
          proximo_evento_fecha: info?.proximo_evento_fecha || null,
          total_presupuestos_confirmados: null,
          ultimo_presupuesto: null
        };
      });
      
      setClientes(clientesList);
      setTotalClientes(clientesList.length);
    } catch (error) {
      console.error('Error fetching clientes:', error);
    } finally {
      setLoading(false);
    }
  };

  // Función para obtener el badge de estado dinámico
  const getEstadoBadge = (cliente) => {
    const estado = cliente.estado_dinamico;
    const dias = cliente.dias_desde_ultimo;

    const estilos = {
      activo: 'bg-green-100 text-green-800',
      reciente: 'bg-yellow-100 text-yellow-800',
      inactivo: 'bg-orange-100 text-orange-800',
      dormido: 'bg-red-100 text-red-800',
      nuevo: 'bg-gray-100 text-gray-600'
    };

    const dots = {
      activo: 'bg-green-500',
      reciente: 'bg-yellow-500',
      inactivo: 'bg-orange-500',
      dormido: 'bg-red-500',
      nuevo: 'bg-gray-400'
    };

    const etiquetas = {
      activo: 'Activo',
      reciente: 'Reciente',
      inactivo: 'Inactivo',
      dormido: 'Dormido',
      nuevo: 'Nuevo'
    };

    const diasFuturo = cliente.dias_hasta_proximo;
    let subtexto = '';
    if (dias !== null && dias !== undefined) {
      if (dias === 0) subtexto = 'Hoy';
      else if (dias === 1) subtexto = 'Ayer';
      else if (dias < 30) subtexto = `Hace ${dias}d`;
      else if (dias < 365) subtexto = `Hace ${Math.round(dias / 30)}m`;
      else subtexto = `Hace ${Math.round(dias / 365)}a`;
    } else if (diasFuturo !== null && diasFuturo !== undefined) {
      if (diasFuturo === 0) subtexto = 'Evento hoy';
      else if (diasFuturo === 1) subtexto = 'Mañana';
      else if (diasFuturo < 30) subtexto = `En ${diasFuturo}d`;
      else subtexto = `En ${Math.round(diasFuturo / 30)}m`;
    } else {
      subtexto = 'Sin eventos';
    }

    return { estilo: estilos[estado], dot: dots[estado], etiqueta: etiquetas[estado], subtexto };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Preparar datos del cliente
      const clienteData = {
        nombre_empresa: formData.nombre_comercial,
        nombre_fiscal: formData.nombre_fiscal,
        cif: formData.cif,
        direccion: formData.direccion,
        direccion_adicional: formData.direccion_adicional,
        email: formData.email,
        telefono: formData.telefono,
        estado: formData.estado || 'activo',
        sector_id: formData.sector_id || null,
        tags: Array.isArray(formData.tags) ? formData.tags : [],
        es_vip: !!formData.es_vip,
        es_administracion_publica: !!formData.es_administracion_publica,
        persona_contacto: formData.contacto_nombre // Para compatibilidad
      };

      let clienteId = editingCliente?.id;
      
      if (editingCliente) {
        await clientesAPI.update(editingCliente.id, clienteData);
      } else {
        const response = await clientesAPI.create(clienteData);
        clienteId = response.data?.id;
      }
      
      // Si hay contacto principal, crearlo
      if (clienteId && formData.contacto_nombre && !editingCliente) {
        try {
          await personasContactoAPI.create({
            cliente_id: clienteId,
            nombre: formData.contacto_nombre,
            cargo: formData.contacto_cargo || '',
            email: formData.contacto_email || '',
            telefono: formData.contacto_telefono || '',
            notas: formData.contacto_notas || '',
            es_principal: true
          });
        } catch (contactoError) {
          console.error('Error creando contacto principal:', contactoError);
        }
      }
      
      fetchClientes();
      fetchTags();
      resetForm();
    } catch (error) {
      console.error('Error saving cliente:', error);
      const detail = error?.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => `${(d.loc || []).slice(1).join('.')}: ${d.msg}`).join('\n')
        : detail;
      alert(msg || 'Error al guardar el cliente');
    }
  };

  const handleRestaurar = async (id) => {
    try {
      await clientesAPI.restaurar(id);
      fetchClientes();
    } catch (error) {
      console.error('Error restaurando cliente:', error);
      alert(error?.response?.data?.detail || 'No se pudo restaurar el cliente.');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Archivar este cliente? Si tiene presupuestos o facturas vinculados se conservará el histórico; si no, se eliminará definitivamente.')) {
      return;
    }
    try {
      const { data } = await clientesAPI.delete(id);
      if (data?.archivado) {
        const p = data.presupuestos_vinculados || 0;
        const f = data.facturas_vinculadas || 0;
        if (p > 0 || f > 0) {
          alert(`Cliente archivado. Tenía ${p} presupuesto(s) y ${f} factura(s) vinculadas — se conservan en el histórico.`);
        }
      }
      fetchClientes();
    } catch (error) {
      console.error('Error deleting cliente:', error);
      alert(error?.response?.data?.detail || 'No se pudo archivar el cliente.');
    }
  };

  const resetForm = () => {
    setFormData({
      nombre_comercial: '',
      nombre_fiscal: '',
      cif: '',
      direccion: '',
      direccion_adicional: '',
      email: '',
      telefono: '',
      estado: 'activo',
      sector_id: '',
      tags: [],
      es_vip: false,
      es_administracion_publica: false,
      contacto_nombre: '',
      contacto_cargo: '',
      contacto_email: '',
      contacto_telefono: '',
      contacto_notas: ''
    });
    setEditingCliente(null);
    setShowModal(false);
  };

  const openEditModal = (cliente) => {
    setEditingCliente(cliente);
    setFormData({
      nombre_comercial: cliente.nombre_empresa || cliente.nombre_comercial || '',
      nombre_fiscal: cliente.nombre_fiscal || '',
      cif: cliente.cif || '',
      direccion: cliente.direccion || '',
      direccion_adicional: cliente.direccion_adicional || '',
      email: cliente.email || '',
      telefono: cliente.telefono || '',
      estado: cliente.estado || 'activo',
      sector_id: cliente.sector_id || '',
      tags: Array.isArray(cliente.tags) ? cliente.tags : [],
      es_vip: !!cliente.es_vip,
      es_administracion_publica: !!cliente.es_administracion_publica,
      contacto_nombre: cliente.persona_contacto || '',
      contacto_cargo: '',
      contacto_email: cliente.email || '',
      contacto_telefono: cliente.telefono || '',
      contacto_notas: ''
    });
    setShowModal(true);
  };

  const openDetallesModal = async (cliente) => {
    setSelectedCliente(cliente);
    setShowDetallesModal(true);
    setLoadingDetalles(true);
    setClienteEstadisticasPago(null);
    setClientePresupuestos([]);
    setClienteInteracciones([]);
    setClienteFacturasPendientes(null);

    try {

      // Cargar estadísticas, estadísticas de pago, personas de contacto, presupuestos, interacciones y facturas pendientes en paralelo
      const [estadisticasRes, estadisticasPagoRes, personasRes, presupuestosRes, interaccionesRes, facturasPendientesRes] = await Promise.all([
        clientesAPI.getEstadisticas(cliente.id),
        clientesAPI.getEstadisticasPago(cliente.id).catch(() => ({ data: null })),
        personasContactoAPI.getAll(cliente.id),
        presupuestosAPI.getAll({ cliente_id: cliente.id, limit: 500 }).catch(() => ({ data: [] })),
        interaccionesAPI.list(cliente.id).catch(() => ({ data: [] })),
        clientesAPI.getFacturasPendientes(cliente.id).catch(() => ({ data: null })),
      ]);

      setClienteEstadisticas(estadisticasRes.data);
      setClienteEstadisticasPago(estadisticasPagoRes.data);
      setClientePersonasContacto(personasRes.data || []);
      const presupuestosData = presupuestosRes.data;
      const presupuestosList = Array.isArray(presupuestosData)
        ? presupuestosData
        : (Array.isArray(presupuestosData?.presupuestos) ? presupuestosData.presupuestos : []);
      setClientePresupuestos(presupuestosList);
      setClienteInteracciones(Array.isArray(interaccionesRes.data) ? interaccionesRes.data : []);
      setClienteFacturasPendientes(facturasPendientesRes.data);
      
    } catch (error) {
      console.error('Error loading detalles:', error);
      alert('Error al cargar los detalles del cliente: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoadingDetalles(false);
    }
  };

  // Funciones para editar contacto principal
  const handleOpenEditContactoPrincipal = () => {
    setContactoPrincipalData({
      persona_contacto: selectedCliente.persona_contacto || selectedCliente.contacto_principal_nombre || '',
      cargo: selectedCliente.cargo_contacto || selectedCliente.contacto_principal_cargo || '',
      email: selectedCliente.email || '',
      telefono: selectedCliente.telefono || selectedCliente.telefono_principal || '',
      extension: selectedCliente.extension_telefono || ''
    });
    setEditandoContactoPrincipal(true);
  };

  const handleSaveContactoPrincipal = async () => {
    try {
      // Enviar datos completos del cliente con los campos de contacto actualizados
      const fullData = {
        nombre_empresa: selectedCliente.nombre_empresa,
        nombre_fiscal: selectedCliente.nombre_fiscal || null,
        persona_contacto: contactoPrincipalData.persona_contacto,
        cargo_contacto: contactoPrincipalData.cargo || null,
        email: contactoPrincipalData.email || null,
        telefono: contactoPrincipalData.telefono || null,
        extension_telefono: contactoPrincipalData.extension || null,
        sector_id: selectedCliente.sector_id || null,
        direccion: selectedCliente.direccion || null,
        direccion_adicional: selectedCliente.direccion_adicional || null,
        ciudad: selectedCliente.ciudad || null,
        provincia: selectedCliente.provincia || null,
        codigo_postal: selectedCliente.codigo_postal || null,
        cif: selectedCliente.cif || null,
        notas: selectedCliente.notas || null,
        estado: selectedCliente.estado || 'activo',
      };
      await clientesAPI.update(selectedCliente.id, fullData);
      
      // Actualizar selectedCliente localmente (incluir cargo como campo extra)
      const merged = {
        ...selectedCliente,
        ...contactoPrincipalData,
        extension_telefono: contactoPrincipalData.extension || null,
      };
      setSelectedCliente(merged);

      // Actualizar en la lista de clientes
      setClientes(clientes.map(c =>
        c.id === selectedCliente.id ? { ...c, ...contactoPrincipalData, extension_telefono: contactoPrincipalData.extension || null } : c
      ));
      
      setEditandoContactoPrincipal(false);
      alert('Contacto principal actualizado correctamente');
    } catch (error) {
      console.error('Error updating contacto principal:', error);
      const detail = error.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map(d => d.msg || d).join(', ') : (detail || error.message);
      alert('Error al actualizar: ' + msg);
    }
  };

  // Funciones para editar contacto adicional
  const handleOpenEditContactoAdicional = (persona) => {
    setEditandoContactoAdicional(persona.id);
    setContactoAdicionalData({
      nombre: persona.nombre || '',
      cargo: persona.cargo || '',
      email: persona.email || '',
      telefono: persona.telefono || '',
      extension: persona.extension || '',
      notas: persona.notas || ''
    });
  };

  const handleSaveContactoAdicional = async () => {
    try {
      // El backend (PersonaContactoCreate) exige cliente_id; sin él devuelve un 422
      await personasContactoAPI.update(editandoContactoAdicional, {
        ...contactoAdicionalData,
        cliente_id: selectedCliente.id,
      });

      // Recargar personas de contacto
      const personasRes = await personasContactoAPI.getAll(selectedCliente.id);
      setClientePersonasContacto(personasRes.data || []);

      setEditandoContactoAdicional(null);
      alert('Contacto actualizado correctamente');
    } catch (error) {
      console.error('Error updating contacto:', error);
      const detail = error.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map(d => d.msg || d).join(', ') : (detail || error.message);
      alert('Error al actualizar: ' + msg);
    }
  };

  const handleDeleteContactoAdicional = async (personaId) => {
    if (!confirm('¿Estás seguro de eliminar este contacto?')) return;
    
    try {
      await personasContactoAPI.delete(personaId);
      
      // Recargar personas de contacto
      const personasRes = await personasContactoAPI.getAll(selectedCliente.id);
      setClientePersonasContacto(personasRes.data || []);
      
      alert('Contacto eliminado correctamente');
    } catch (error) {
      console.error('Error deleting contacto:', error);
      alert('Error al eliminar: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDeleteContacto = async (personaId) => {
    if (!window.confirm('¿Estás seguro de eliminar este contacto?')) return;
    
    try {
      await personasContactoAPI.delete(personaId);
      
      // Recargar personas de contacto
      const personasRes = await personasContactoAPI.getAll(selectedCliente.id);
      setClientePersonasContacto(personasRes.data || []);
      
      alert('Contacto eliminado correctamente');
    } catch (error) {
      console.error('Error deleting contacto:', error);
      alert('Error al eliminar: ' + (error.response?.data?.detail || error.message));
    }
  };
  
  // Funciones para editar datos de la empresa
  const handleOpenEditEmpresa = () => {
    // Consolidamos dirección + ciudad/provincia/CP antiguas en un único campo libre
    const partes = [
      selectedCliente.direccion,
      selectedCliente.ciudad,
      selectedCliente.provincia,
      selectedCliente.codigo_postal,
    ].filter(Boolean);
    const direccionCompuesta = selectedCliente.direccion && (
      (selectedCliente.ciudad && selectedCliente.direccion.includes(selectedCliente.ciudad)) ||
      (selectedCliente.codigo_postal && selectedCliente.direccion.includes(selectedCliente.codigo_postal))
    )
      ? selectedCliente.direccion
      : partes.join(', ');
    setEmpresaData({
      nombre_empresa: selectedCliente.nombre_empresa || '',
      nombre_fiscal: selectedCliente.nombre_fiscal || '',
      cif: selectedCliente.cif || '',
      direccion: direccionCompuesta || '',
      sector_id: selectedCliente.sector_id || '',
      tags: Array.isArray(selectedCliente.tags) ? selectedCliente.tags : [],
      es_vip: !!selectedCliente.es_vip,
    });
    setEditandoEmpresa(true);
  };
  
  const handleSaveEmpresa = async () => {
    try {
      // Enviamos dirección unificada y limpiamos campos deprecados (nombre_comercial, ciudad, provincia, CP)
      const payload = { ...empresaData, nombre_comercial: null, ciudad: null, provincia: null, codigo_postal: null };
      await clientesAPI.update(selectedCliente.id, payload);

      // Actualizar selectedCliente localmente
      setSelectedCliente({
        ...selectedCliente,
        ...payload,
      });

      // Actualizar en la lista de clientes
      setClientes(clientes.map(c =>
        c.id === selectedCliente.id ? { ...c, ...payload } : c
      ));

      setEditandoEmpresa(false);
      fetchTags();
      alert('Datos de la empresa actualizados correctamente');
    } catch (error) {
      console.error('Error updating empresa:', error);
      const detail = error.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map(d => d.msg || d).join(', ') : (detail || error.message);
      alert('Error al actualizar: ' + msg);
    }
  };
  
  // Funciones para añadir nuevo contacto
  const handleAddContacto = async () => {
    if (!nuevoContactoData.nombre) {
      alert('El nombre del contacto es obligatorio');
      return;
    }
    
    try {
      await personasContactoAPI.create({
        ...nuevoContactoData,
        cliente_id: selectedCliente.id
      });
      
      // Recargar personas de contacto
      const personasRes = await personasContactoAPI.getAll(selectedCliente.id);
      setClientePersonasContacto(personasRes.data || []);
      
      // Resetear y cerrar modal
      setNuevoContactoData({
        nombre: '',
        cargo: '',
        email: '',
        telefono: '',
        extension: '',
        notas: ''
      });
      setShowAddContactoModal(false);
      alert('Contacto añadido correctamente');
    } catch (error) {
      console.error('Error adding contacto:', error);
      const detail = error.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map(d => d.msg || d).join(', ') : (detail || error.message);
      alert('Error al añadir: ' + msg);
    }
  };

  const sectorNombrePorId = Object.fromEntries(sectores.map(s => [s.id, s.nombre]));

  const filteredClientes = clientes.filter(cliente => {
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch =
      cliente.nombre_empresa?.toLowerCase().includes(searchLower) ||
      cliente.nombre_comercial?.toLowerCase().includes(searchLower) ||
      cliente.nombre_fiscal?.toLowerCase().includes(searchLower) ||
      cliente.cif?.toLowerCase().includes(searchLower) ||
      false;
    const matchesEstado = !filterEstado || cliente.estado_dinamico === filterEstado;
    const matchesSector = !filterSector
      || (filterSector === '__none__' ? !cliente.sector_id : cliente.sector_id === filterSector);
    if (!matchesSector) return false;
    if (soloVip && !cliente.es_vip) return false;
    if (filterTag) {
      const tags = Array.isArray(cliente.tags) ? cliente.tags : [];
      if (!tags.includes(filterTag)) return false;
    }
    return matchesSearch && matchesEstado;
  });
  
  // Calcular paginación
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentClientes = filteredClientes.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredClientes.length / itemsPerPage);

  // Contadores por estado dinámico
  const contadorEstados = {
    activo: clientes.filter(c => c.estado_dinamico === 'activo').length,
    reciente: clientes.filter(c => c.estado_dinamico === 'reciente').length,
    inactivo: clientes.filter(c => c.estado_dinamico === 'inactivo').length,
    dormido: clientes.filter(c => c.estado_dinamico === 'dormido').length,
    nuevo: clientes.filter(c => c.estado_dinamico === 'nuevo').length,
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">Cargando...</div>;
  }

  return (
    <div>
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900" data-testid="clientes-title">CRM - Clientes</h1>
          <p className="text-gray-600 mt-1">Gestión de clientes y empresas</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setMostrarArchivados(v => !v)}
            title={mostrarArchivados ? 'Volver a clientes activos' : 'Ver clientes archivados'}
            className={`px-3 py-2 rounded-lg flex items-center space-x-2 text-sm transition-colors border ${
              mostrarArchivados
                ? 'bg-amber-100 border-amber-300 text-amber-800 hover:bg-amber-200'
                : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            <Archive className="w-4 h-4" />
            <span>{mostrarArchivados ? 'Ver activos' : 'Ver archivados'}</span>
          </button>
          <button
            onClick={() => setShowModal(true)}
            data-testid="new-client-button"
            data-uso="clientes.nuevo"
            className="bg-duetto-primary text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-opacity-90 transition-colors"
          >
            <Plus className="w-5 h-5" />
            <span>Nuevo Cliente</span>
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar por nombre, CIF..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="flex items-center gap-1 flex-wrap">
            <select
              value={filterSector}
              onChange={(e) => setFilterSector(e.target.value)}
              className="px-2 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              title="Filtrar por sector"
            >
              <option value="">Todos los sectores</option>
              <option value="__none__">Sin sector asignado</option>
              {sectores.map(s => (
                <option key={s.id} value={s.id}>{s.nombre}</option>
              ))}
            </select>
            <button
              onClick={() => setShowSectoresModal(true)}
              title="Gestionar sectores"
              className="p-2 border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50"
            >
              <Settings2 className="w-4 h-4" />
            </button>
            {tagsDisponibles.length > 0 && (
              <select
                value={filterTag}
                onChange={(e) => setFilterTag(e.target.value)}
                className="px-2 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                title="Filtrar por etiqueta"
              >
                <option value="">Todas las etiquetas</option>
                {tagsDisponibles.map(t => (
                  <option key={t.tag} value={t.tag}>{t.tag} ({t.count})</option>
                ))}
              </select>
            )}
            <button
              onClick={() => setSoloVip(v => !v)}
              title="Solo clientes VIP"
              className={`px-2 py-2 border rounded-lg text-sm flex items-center gap-1 transition ${
                soloVip
                  ? 'bg-yellow-100 border-yellow-400 text-yellow-800'
                  : 'border-gray-300 text-gray-600 hover:bg-gray-50'
              }`}
            >
              <Star className={`w-4 h-4 ${soloVip ? 'fill-yellow-400 text-yellow-500' : ''}`} />
              VIP
            </button>
          </div>
          <div className="flex flex-wrap gap-1.5">
            <button
              onClick={() => setFilterEstado('')}
              title="Ver todos los clientes"
              className={`px-2 py-1 rounded text-xs font-medium transition ${
                filterEstado === '' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Todos ({clientes.length})
            </button>
            <button
              onClick={() => setFilterEstado('activo')}
              title="Último evento hace menos de 30 días"
              className={`inline-flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium transition ${
                filterEstado === 'activo' ? 'bg-green-600 text-white' : 'bg-green-100 text-green-800 hover:bg-green-200'
              }`}
            >
              <span className={`w-2 h-2 rounded-full ${filterEstado === 'activo' ? 'bg-white' : 'bg-green-500'}`}></span>
              Activo ({contadorEstados.activo})
            </button>
            <button
              onClick={() => setFilterEstado('reciente')}
              title="Último evento hace 31-90 días"
              className={`inline-flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium transition ${
                filterEstado === 'reciente' ? 'bg-yellow-600 text-white' : 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
              }`}
            >
              <span className={`w-2 h-2 rounded-full ${filterEstado === 'reciente' ? 'bg-white' : 'bg-yellow-500'}`}></span>
              Reciente ({contadorEstados.reciente})
            </button>
            <button
              onClick={() => setFilterEstado('inactivo')}
              title="Último evento hace 91-180 días"
              className={`inline-flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium transition ${
                filterEstado === 'inactivo' ? 'bg-orange-600 text-white' : 'bg-orange-100 text-orange-800 hover:bg-orange-200'
              }`}
            >
              <span className={`w-2 h-2 rounded-full ${filterEstado === 'inactivo' ? 'bg-white' : 'bg-orange-500'}`}></span>
              Inactivo ({contadorEstados.inactivo})
            </button>
            <button
              onClick={() => setFilterEstado('dormido')}
              title="Último evento hace más de 180 días"
              className={`inline-flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium transition ${
                filterEstado === 'dormido' ? 'bg-red-600 text-white' : 'bg-red-100 text-red-800 hover:bg-red-200'
              }`}
            >
              <span className={`w-2 h-2 rounded-full ${filterEstado === 'dormido' ? 'bg-white' : 'bg-red-500'}`}></span>
              Dormido ({contadorEstados.dormido})
            </button>
            <button
              onClick={() => setFilterEstado('nuevo')}
              title="Sin eventos confirmados"
              className={`inline-flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium transition ${
                filterEstado === 'nuevo' ? 'bg-gray-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <span className={`w-2 h-2 rounded-full ${filterEstado === 'nuevo' ? 'bg-white' : 'bg-gray-400'}`}></span>
              Nuevo ({contadorEstados.nuevo})
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/4">Empresa</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">Contacto</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email / Teléfono</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden lg:table-cell">Dirección</th>
                <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-32">Actividad</th>
                <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-10"></th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {currentClientes.map((cliente) => (
                <tr
                  key={cliente.id}
                  onClick={() => openDetallesModal(cliente)}
                  className="hover:bg-gray-50 cursor-pointer"
                  data-uso="clientes.abrir"
                >
                  <td className="px-4 py-1.5">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {cliente.es_vip && (
                        <Star className="w-3.5 h-3.5 fill-yellow-400 text-yellow-500 flex-shrink-0" title="Cliente VIP" />
                      )}
                      <span className={`text-sm font-medium ${cliente.estado === 'archivado' ? 'text-gray-400 line-through' : 'text-gray-900'}`}>
                        {cliente.nombre_empresa}
                      </span>
                      {cliente.estado === 'archivado' && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-700">
                          archivado
                        </span>
                      )}
                      {Array.isArray(cliente.tags) && cliente.tags.slice(0, 3).map(t => (
                        <span key={t} className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-indigo-100 text-indigo-700">
                          {t}
                        </span>
                      ))}
                      {Array.isArray(cliente.tags) && cliente.tags.length > 3 && (
                        <span className="text-[10px] text-gray-500">+{cliente.tags.length - 3}</span>
                      )}
                    </div>
                    {/* Mostrar contacto en móvil */}
                    <div className="md:hidden text-xs text-gray-500">
                      {cliente.persona_contacto || ''}
                    </div>
                  </td>
                  <td className="px-3 py-1.5 hidden md:table-cell">
                    <div className="text-sm text-gray-900">{cliente.persona_contacto || '-'}</div>
                  </td>
                  <td className="px-3 py-1.5">
                    <div className="flex flex-col gap-0.5">
                      {cliente.email && (
                        <div className="flex items-center text-sm text-gray-600 gap-1">
                          <a
                            href={construirMailto(cliente.email)}
                            onClick={(e) => e.stopPropagation()}
                            title={`Enviar email a ${cliente.email}`}
                            className="flex items-center hover:text-blue-600"
                          >
                            <Mail className="w-3.5 h-3.5 mr-1 flex-shrink-0" />
                            <span className="truncate max-w-[150px] lg:max-w-none">{cliente.email}</span>
                          </a>
                        </div>
                      )}
                      {cliente.telefono && (
                        <div className="flex items-center text-sm text-gray-600 gap-1.5">
                          <a
                            href={`tel:${cliente.telefono}`}
                            onClick={(e) => e.stopPropagation()}
                            title={`Llamar ${cliente.telefono}`}
                            className="flex items-center hover:text-blue-600"
                          >
                            <Phone className="w-3.5 h-3.5 mr-1 flex-shrink-0" />
                            {cliente.telefono}
                          </a>
                          {construirWhatsapp(cliente.telefono) && (
                            <a
                              href={construirWhatsapp(cliente.telefono)}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              title="Abrir WhatsApp"
                              className="text-green-600 hover:text-green-700"
                            >
                              <MessageCircle className="w-3.5 h-3.5" />
                            </a>
                          )}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-1.5 hidden lg:table-cell">
                    <div className="text-sm text-gray-900 truncate max-w-[200px]">{cliente.direccion ? cliente.direccion.split(',')[0] : '-'}</div>
                  </td>
                  <td className="px-2 py-1.5">
                    {(() => {
                      const badge = getEstadoBadge(cliente);
                      return (
                        <div className="flex items-center justify-center gap-1.5" title={`${badge.etiqueta} · ${badge.subtexto}`}>
                          <span className={`w-2 h-2 rounded-full flex-shrink-0 ${badge.dot}`}></span>
                          <span className="text-xs text-gray-600 whitespace-nowrap">{badge.subtexto}</span>
                        </div>
                      );
                    })()}
                  </td>
                  <td className="px-2 py-1.5 text-right">
                    {cliente.estado === 'archivado' ? (
                      <button
                        onClick={(e) => { e.stopPropagation(); handleRestaurar(cliente.id); }}
                        className="text-green-700 hover:text-green-900 p-1"
                        title="Restaurar cliente"
                      >
                        <RotateCcw className="w-4 h-4" />
                      </button>
                    ) : (
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDelete(cliente.id); }}
                        className="text-red-600 hover:text-red-900 p-1"
                        title="Archivar / eliminar cliente"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filteredClientes.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            No se encontraron clientes
          </div>
        )}
      </div>
      
      {/* Paginación */}
      {filteredClientes.length > 0 && (
        <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6 rounded-b-lg">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Anterior
            </button>
            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Siguiente
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Mostrando <span className="font-medium">{indexOfFirstItem + 1}</span> a <span className="font-medium">{Math.min(indexOfLastItem, filteredClientes.length)}</span> de{' '}
                <span className="font-medium">{filteredClientes.length}</span> clientes
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Anterior
                </button>
                {(() => {
                  const pages = [];
                  const maxVisible = 7;
                  if (totalPages <= maxVisible) {
                    for (let i = 1; i <= totalPages; i++) pages.push(i);
                  } else {
                    pages.push(1);
                    const start = Math.max(2, currentPage - 1);
                    const end = Math.min(totalPages - 1, currentPage + 1);
                    if (start > 2) pages.push('…-start');
                    for (let i = start; i <= end; i++) pages.push(i);
                    if (end < totalPages - 1) pages.push('…-end');
                    pages.push(totalPages);
                  }
                  return pages.map((p, idx) =>
                    typeof p === 'number' ? (
                      <button
                        key={`p-${p}`}
                        onClick={() => setCurrentPage(p)}
                        className={`relative inline-flex items-center px-3 py-2 border text-sm font-medium ${
                          currentPage === p
                            ? 'z-10 bg-duetto-primary border-duetto-primary text-white'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {p}
                      </button>
                    ) : (
                      <span
                        key={`e-${idx}`}
                        className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm text-gray-400 select-none"
                      >
                        …
                      </span>
                    )
                  );
                })()}
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Siguiente
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-900">
                {editingCliente ? 'Editar Cliente' : 'Nuevo Cliente'}
              </h2>
              <button onClick={resetForm} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6">
              {/* Sección: Datos de la Empresa */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <Building className="w-5 h-5 mr-2 text-blue-600" />
                  Datos de la Empresa
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nombre Comercial *</label>
                    <input
                      type="text"
                      value={formData.nombre_comercial}
                      onChange={(e) => setFormData({ ...formData, nombre_comercial: e.target.value })}
                      placeholder="Nombre con el que se conoce la empresa"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="flex justify-between text-sm font-medium text-gray-700 mb-1">
                      <span>Nombre Fiscal</span>
                      <span className={(formData.nombre_fiscal || '').length > 80 ? 'text-amber-600 font-semibold' : 'text-gray-400'}>
                        {(formData.nombre_fiscal || '').length}/80
                      </span>
                    </label>
                    <input
                      type="text"
                      value={formData.nombre_fiscal}
                      onChange={(e) => setFormData({ ...formData, nombre_fiscal: e.target.value })}
                      placeholder="Razón social (para facturas)"
                      maxLength={80}
                      title="Razón social que viaja como CorporateName en FACe/Facturae. Máximo 80 caracteres o la administración rechaza la factura."
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">CIF / NIF</label>
                    <input
                      type="text"
                      value={formData.cif}
                      onChange={(e) => setFormData({ ...formData, cif: e.target.value.toUpperCase() })}
                      placeholder="Ej: B12345678"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email de la Empresa</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="info@empresa.com"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Teléfono</label>
                    <input
                      type="text"
                      value={formData.telefono}
                      onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                      placeholder="Ej: 912345678"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                      <Tag className="w-3.5 h-3.5" /> Sector
                    </label>
                    <div className="flex items-center gap-2">
                      <select
                        value={formData.sector_id}
                        onChange={(e) => setFormData({ ...formData, sector_id: e.target.value })}
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Sin sector</option>
                        {sectores.map(s => (
                          <option key={s.id} value={s.id}>{s.nombre}</option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={() => setShowSectoresModal(true)}
                        title="Gestionar sectores"
                        className="px-3 py-2 border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50"
                      >
                        <Settings2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                      <Tag className="w-3.5 h-3.5" /> Etiquetas
                    </label>
                    <TagsInput
                      value={formData.tags}
                      onChange={(tags) => setFormData({ ...formData, tags })}
                      sugerencias={tagsDisponibles}
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="inline-flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.es_vip}
                        onChange={(e) => setFormData({ ...formData, es_vip: e.target.checked })}
                        className="w-4 h-4 text-yellow-500 border-gray-300 rounded focus:ring-yellow-400"
                      />
                      <Star className={`w-4 h-4 ${formData.es_vip ? 'fill-yellow-400 text-yellow-500' : 'text-gray-400'}`} />
                      <span className="text-sm font-medium text-gray-700">Marcar como cliente VIP</span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Sección: Domicilio Social */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <MapPin className="w-5 h-5 mr-2 text-green-600" />
                  Domicilio Social
                </h3>
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Dirección</label>
                    <DireccionAutocomplete
                      id="cliente-direccion-autocomplete"
                      value={formData.direccion}
                      onChange={(direccion) => setFormData(prev => ({ ...prev, direccion }))}
                      placeholder="Escribe para buscar dirección..."
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">Empieza a escribir y selecciona de las sugerencias</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Información adicional</label>
                    <input
                      type="text"
                      value={formData.direccion_adicional}
                      onChange={(e) => setFormData({ ...formData, direccion_adicional: e.target.value })}
                      placeholder="Piso, puerta, escalera, edificio..."
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>

              {/* Sección: Contacto Principal */}
              {!editingCliente && (
                <div className="mb-6 bg-blue-50 rounded-lg p-4 border border-blue-200">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                    <User className="w-5 h-5 mr-2 text-blue-600" />
                    Contacto Principal
                    <span className="ml-2 text-xs font-normal text-gray-500">(Opcional)</span>
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Nombre Completo</label>
                      <input
                        type="text"
                        value={formData.contacto_nombre}
                        onChange={(e) => setFormData({ ...formData, contacto_nombre: e.target.value })}
                        placeholder="Nombre y apellidos"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Cargo</label>
                      <input
                        type="text"
                        value={formData.contacto_cargo}
                        onChange={(e) => setFormData({ ...formData, contacto_cargo: e.target.value })}
                        placeholder="Ej: Director de Eventos"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email del Contacto</label>
                      <input
                        type="email"
                        value={formData.contacto_email}
                        onChange={(e) => setFormData({ ...formData, contacto_email: e.target.value })}
                        placeholder="contacto@empresa.com"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Teléfono del Contacto</label>
                      <input
                        type="text"
                        value={formData.contacto_telefono}
                        onChange={(e) => setFormData({ ...formData, contacto_telefono: e.target.value })}
                        placeholder="Móvil o fijo"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Notas</label>
                      <textarea
                        value={formData.contacto_notas}
                        onChange={(e) => setFormData({ ...formData, contacto_notas: e.target.value })}
                        placeholder="Observaciones sobre este contacto..."
                        rows="2"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                      />
                    </div>
                  </div>
                </div>
              )}

              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  {editingCliente ? 'Actualizar Cliente' : 'Crear Cliente'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de Detalles y Estadísticas */}
      {showDetallesModal && selectedCliente && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="sticky top-0 bg-gradient-to-r from-duetto-primary to-duetto-secondary text-white px-6 py-4 flex justify-between items-center z-10">
              <div>
                <h2 className="text-2xl font-bold">{selectedCliente.nombre_empresa}</h2>
                <p className="text-sm opacity-90">Detalles del Cliente y Estadísticas</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    setShowDetallesModal(false);
                    navigate(`/presupuestos?nuevo=1&cliente_id=${selectedCliente.id}`);
                  }}
                  className="flex items-center gap-2 bg-white text-duetto-primary hover:bg-gray-100 px-3 py-2 rounded-lg text-sm font-semibold transition"
                  title="Crear nuevo presupuesto para este cliente"
                >
                  <FilePlus className="w-4 h-4" />
                  <span>Nuevo presupuesto</span>
                </button>
                <button
                  onClick={() => setShowDetallesModal(false)}
                  className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-2"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            {loadingDetalles ? (
              <div className="p-12 text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-duetto-primary mx-auto"></div>
                <p className="text-gray-600 mt-4">Cargando estadísticas...</p>
              </div>
            ) : (
              <div className="p-6">
                {/* Datos de la Empresa */}
                <div className="mb-6">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2"><Building className="w-5 h-5 text-gray-600" /> Datos de la Empresa</h3>
                    {!editandoEmpresa && (
                      <button
                        onClick={handleOpenEditEmpresa}
                        className="text-xs px-3 py-1 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition flex items-center gap-1"
                      >
                        <Edit className="w-3 h-3" />
                        Editar
                      </button>
                    )}
                  </div>
                  
                  {editandoEmpresa ? (
                    <div className="bg-blue-50 border-2 border-blue-300 rounded-lg p-5 space-y-3">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Nombre Comercial</label>
                          <input
                            type="text"
                            value={empresaData.nombre_empresa}
                            onChange={(e) => setEmpresaData({...empresaData, nombre_empresa: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                            placeholder="Nombre con el que se conoce"
                          />
                        </div>
                        <div>
                          <label className="flex justify-between text-xs font-medium text-gray-700 mb-1">
                            <span>Nombre Fiscal</span>
                            <span className={(empresaData.nombre_fiscal || '').length > 80 ? 'text-amber-600 font-semibold' : 'text-gray-400'}>
                              {(empresaData.nombre_fiscal || '').length}/80
                            </span>
                          </label>
                          <input
                            type="text"
                            value={empresaData.nombre_fiscal}
                            onChange={(e) => setEmpresaData({...empresaData, nombre_fiscal: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                            placeholder="Razón social legal (para facturación)"
                            maxLength={80}
                            title="Razón social que viaja como CorporateName en FACe/Facturae. Máximo 80 caracteres o la administración rechaza la factura."
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">CIF/NIF</label>
                          <input
                            type="text"
                            value={empresaData.cif}
                            onChange={(e) => setEmpresaData({...empresaData, cif: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          />
                        </div>
                        <div className="md:col-span-2">
                          <label className="block text-xs font-medium text-gray-700 mb-1">Dirección</label>
                          <DireccionAutocomplete
                            id="empresa-direccion-autocomplete"
                            value={empresaData.direccion}
                            onChange={(direccion) => setEmpresaData(prev => ({ ...prev, direccion }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                            placeholder="Empieza a escribir y selecciona una sugerencia…"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Se autocompleta con Google Maps. Puedes editarla después para añadir piso, puerta, despacho, etc.
                          </p>
                        </div>
                        <div className="md:col-span-2">
                          <label className="block text-xs font-medium text-gray-700 mb-1 flex items-center gap-1">
                            <Tag className="w-3 h-3" /> Sector
                          </label>
                          <div className="flex items-center gap-2">
                            <select
                              value={empresaData.sector_id || ''}
                              onChange={(e) => setEmpresaData({...empresaData, sector_id: e.target.value})}
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                            >
                              <option value="">Sin sector</option>
                              {sectores.map(s => (
                                <option key={s.id} value={s.id}>{s.nombre}</option>
                              ))}
                            </select>
                            <button
                              type="button"
                              onClick={() => setShowSectoresModal(true)}
                              title="Gestionar sectores"
                              className="px-3 py-2 border border-gray-300 rounded-lg text-gray-600 hover:bg-white"
                            >
                              <Settings2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                        <div className="md:col-span-2">
                          <label className="block text-xs font-medium text-gray-700 mb-1 flex items-center gap-1">
                            <Tag className="w-3 h-3" /> Etiquetas
                          </label>
                          <TagsInput
                            value={empresaData.tags}
                            onChange={(tags) => setEmpresaData({ ...empresaData, tags })}
                            sugerencias={tagsDisponibles}
                          />
                        </div>
                        <div className="md:col-span-2">
                          <label className="inline-flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={!!empresaData.es_vip}
                              onChange={(e) => setEmpresaData({ ...empresaData, es_vip: e.target.checked })}
                              className="w-4 h-4 text-yellow-500 border-gray-300 rounded focus:ring-yellow-400"
                            />
                            <Star className={`w-4 h-4 ${empresaData.es_vip ? 'fill-yellow-400 text-yellow-500' : 'text-gray-400'}`} />
                            <span className="text-sm font-medium text-gray-700">Cliente VIP</span>
                          </label>
                        </div>
                      </div>
                      <div className="flex gap-2 justify-end pt-2">
                        <button
                          onClick={() => setEditandoEmpresa(false)}
                          className="px-4 py-2 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                        >
                          Cancelar
                        </button>
                        <button
                          onClick={handleSaveEmpresa}
                          className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                        >
                          Guardar
                        </button>
                      </div>
                    </div>
                  ) : (
                  <div className="bg-gradient-to-br from-gray-50 to-gray-100 border-2 border-gray-300 rounded-lg p-5">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Columna izquierda */}
                      <div className="space-y-4">
                        <div className="flex items-start">
                          <div className="flex-shrink-0 w-32">
                            <span className="text-xs font-semibold text-gray-600 uppercase">Nombre Comercial</span>
                          </div>
                          <div className="flex-1">
                            <p className="text-sm font-semibold text-gray-900">{selectedCliente.nombre_empresa}</p>
                          </div>
                        </div>

                        {selectedCliente.nombre_fiscal && (
                          <div className="flex items-start">
                            <div className="flex-shrink-0 w-32">
                              <span className="text-xs font-semibold text-gray-600 uppercase">Nombre Fiscal</span>
                            </div>
                            <div className="flex-1">
                              <p className="text-sm text-gray-900">{selectedCliente.nombre_fiscal}</p>
                            </div>
                          </div>
                        )}
                        
                        {selectedCliente.cif && (
                          <div className="flex items-start">
                            <div className="flex-shrink-0 w-32">
                              <span className="text-xs font-semibold text-gray-600 uppercase">CIF/NIF</span>
                            </div>
                            <div className="flex-1">
                              <p className="text-sm font-semibold text-gray-900">{selectedCliente.cif}</p>
                            </div>
                          </div>
                        )}
                        
                        <div className="flex items-start">
                          <div className="flex-shrink-0 w-32">
                            <span className="text-xs font-semibold text-gray-600 uppercase">Dirección</span>
                          </div>
                          <div className="flex-1">
                            <p className="text-sm text-gray-900">
                              {[selectedCliente.direccion, selectedCliente.ciudad, selectedCliente.provincia, selectedCliente.codigo_postal].filter(Boolean).join(', ') || 'No especificada'}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-start">
                          <div className="flex-shrink-0 w-32">
                            <span className="text-xs font-semibold text-gray-600 uppercase">Sector</span>
                          </div>
                          <div className="flex-1">
                            {selectedCliente.sector_id && sectorNombrePorId[selectedCliente.sector_id] ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                <Tag className="w-3 h-3" />
                                {sectorNombrePorId[selectedCliente.sector_id]}
                              </span>
                            ) : (
                              <span className="text-sm text-gray-400">Sin sector</span>
                            )}
                          </div>
                        </div>

                        <div className="flex items-start">
                          <div className="flex-shrink-0 w-32">
                            <span className="text-xs font-semibold text-gray-600 uppercase">Etiquetas</span>
                          </div>
                          <div className="flex-1 flex flex-wrap gap-1">
                            {selectedCliente.es_vip && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-800">
                                <Star className="w-3 h-3 fill-yellow-400" /> VIP
                              </span>
                            )}
                            {Array.isArray(selectedCliente.tags) && selectedCliente.tags.length > 0 ? (
                              selectedCliente.tags.map(t => (
                                <span key={t} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                                  {t}
                                </span>
                              ))
                            ) : (!selectedCliente.es_vip && (
                              <span className="text-sm text-gray-400">Sin etiquetas</span>
                            ))}
                          </div>
                        </div>
                      </div>
                      
                      {/* Columna derecha */}
                      <div className="space-y-4">
                        <div className="flex items-start">
                          <div className="flex-shrink-0 w-32">
                            <span className="text-xs font-semibold text-gray-600 uppercase">Estado</span>
                          </div>
                          <div className="flex-1">
                            <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
                              selectedCliente.estado === 'activo' ? 'bg-green-100 text-green-800' :
                              selectedCliente.estado === 'lead' ? 'bg-blue-100 text-blue-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {selectedCliente.estado?.toUpperCase() || 'N/A'}
                            </span>
                          </div>
                        </div>
                        
                        {selectedCliente.fecha_alta && (
                          <div className="flex items-start">
                            <div className="flex-shrink-0 w-32">
                              <span className="text-xs font-semibold text-gray-600 uppercase">Fecha Alta</span>
                            </div>
                            <div className="flex-1">
                              <p className="text-sm text-gray-900">
                                {new Date(selectedCliente.fecha_alta).toLocaleDateString('es-ES', {
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric'
                                })}
                              </p>
                            </div>
                          </div>
                        )}
                        
                        {selectedCliente.notas && (
                          <div className="flex items-start">
                            <div className="flex-shrink-0 w-32">
                              <span className="text-xs font-semibold text-gray-600 uppercase">Notas</span>
                            </div>
                            <div className="flex-1">
                              <p className="text-sm text-gray-700 italic">{selectedCliente.notas}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  )
                }
                </div>

                {/* Facturación FACe / destinos DIR3 */}
                {selectedCliente?.id && <FaceDestinos clienteId={selectedCliente.id} />}

                {/* Estadísticas Grid */}
                {clienteEstadisticas && (
                  <>
                    <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2"><BarChart3 className="w-5 h-5 text-gray-600" /> Estadísticas del Cliente</h3>
                    
                    {/* Resumen Principal */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <DollarSign className="w-8 h-8 text-blue-600" />
                          <span className="text-xs text-blue-600 font-semibold">TICKET MEDIO</span>
                        </div>
                        <p className="text-2xl font-bold text-blue-900">€{clienteEstadisticas.ticket_medio.toFixed(2)}</p>
                        <p className="text-xs text-blue-700 mt-1">Por evento confirmado, sin IVA</p>
                      </div>

                      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <Users className="w-8 h-8 text-green-600" />
                          <span className="text-xs text-green-600 font-semibold">PERSONAS MEDIA</span>
                        </div>
                        <p className="text-2xl font-bold text-green-900">{clienteEstadisticas.personas_media}</p>
                        <p className="text-xs text-green-700 mt-1">Por evento</p>
                      </div>

                      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <Clock className="w-8 h-8 text-purple-600" />
                          <span className="text-xs text-purple-600 font-semibold">DÍAS PAGO</span>
                        </div>
                        <p className="text-2xl font-bold text-purple-900">
                          {clienteEstadisticasPago?.tiene_historial
                            ? Math.round(clienteEstadisticasPago.media_dias_pago)
                            : (clienteEstadisticas.dias_medio_pago || '—')}
                        </p>
                        <p className="text-xs text-purple-700 mt-1">
                          {clienteEstadisticasPago?.tiene_historial
                            ? `Media (${clienteEstadisticasPago.total_pagos} pago${clienteEstadisticasPago.total_pagos === 1 ? '' : 's'})`
                            : 'Días medios de pago'}
                        </p>
                      </div>

                      <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <Calendar className="w-8 h-8 text-orange-600" />
                          <span className="text-xs text-orange-600 font-semibold">
                            {clienteEstadisticas.dias_desde_ultimo_evento !== null
                              ? 'ÚLTIMO EVENTO'
                              : clienteEstadisticas.dias_hasta_proximo_evento !== null
                                ? 'PRÓXIMO EVENTO'
                                : 'EVENTOS'}
                          </span>
                        </div>
                        {(() => {
                          const diasPasado = clienteEstadisticas.dias_desde_ultimo_evento;
                          const diasFuturo = clienteEstadisticas.dias_hasta_proximo_evento;
                          if (diasPasado !== null && diasPasado !== undefined) {
                            return (
                              <>
                                <p className="text-2xl font-bold text-orange-900">
                                  {diasPasado === 0 ? 'Hoy' : diasPasado === 1 ? 'Ayer' : `${diasPasado} días`}
                                </p>
                                <p className="text-xs text-orange-700 mt-1">
                                  {/* Multievento (M-82): el rango de fechas de sus jornadas */}
                                  {clienteEstadisticas.ultimo_evento_rango
                                    ? (diasPasado === 0 || diasPasado === 1
                                      ? clienteEstadisticas.ultimo_evento_rango
                                      : `Hace (${clienteEstadisticas.ultimo_evento_rango})`)
                                    : (diasPasado === 0 || diasPasado === 1
                                      ? (clienteEstadisticas.ultimo_evento_fecha || '')
                                      : 'Hace')}
                                </p>
                              </>
                            );
                          }
                          if (diasFuturo !== null && diasFuturo !== undefined) {
                            return (
                              <>
                                <p className="text-2xl font-bold text-orange-900">
                                  {diasFuturo === 0 ? 'Hoy' : diasFuturo === 1 ? 'Mañana' : `En ${diasFuturo} días`}
                                </p>
                                <p className="text-xs text-orange-700 mt-1">
                                  {clienteEstadisticas.proximo_evento_rango
                                    ? clienteEstadisticas.proximo_evento_rango
                                    : (clienteEstadisticas.proximo_evento_fecha
                                      ? new Date(clienteEstadisticas.proximo_evento_fecha).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' })
                                      : 'Programado')}
                                </p>
                              </>
                            );
                          }
                          return (
                            <>
                              <p className="text-2xl font-bold text-orange-900">N/A</p>
                              <p className="text-xs text-orange-700 mt-1">Sin eventos</p>
                            </>
                          );
                        })()}
                      </div>
                    </div>

                    {/* Facturación */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                      <div className="bg-gradient-to-br from-duetto-primary to-duetto-secondary text-white rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <TrendingUp className="w-6 h-6" />
                          <span className="text-xs font-semibold opacity-90">AÑO ACTUAL</span>
                        </div>
                        <p className="text-3xl font-bold">€{clienteEstadisticas.facturacion_year_current.toFixed(2)}</p>
                        <p className="text-sm opacity-90 mt-1">{clienteEstadisticas.eventos_year_current} eventos</p>
                      </div>

                      <div className="bg-gray-100 border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <TrendingUp className="w-6 h-6 text-gray-600" />
                          <span className="text-xs text-gray-600 font-semibold">AÑO ANTERIOR</span>
                        </div>
                        <p className="text-3xl font-bold text-gray-900">€{clienteEstadisticas.facturacion_year_previous.toFixed(2)}</p>
                        <p className="text-sm text-gray-700 mt-1">{clienteEstadisticas.eventos_year_previous} eventos</p>
                      </div>

                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <BarChart3 className="w-6 h-6 text-yellow-600" />
                          <span className="text-xs text-yellow-600 font-semibold">CONVERSIÓN</span>
                        </div>
                        <p className="text-3xl font-bold text-yellow-900">{clienteEstadisticas.tasa_conversion}%</p>
                        <p className="text-sm text-yellow-700 mt-1">
                          {clienteEstadisticas.presupuestos_confirmados}/{clienteEstadisticas.total_presupuestos} confirmados
                        </p>
                      </div>
                    </div>

                    {/* Facturas emitidas del cliente (con resumen de deuda viva) */}
                    <FacturasClienteCard
                      data={clienteFacturasPendientes}
                      onVerFactura={(facturaId) => navigate(`/finanzas?factura=${facturaId}`)}
                    />

                    {/* Timeline de interacciones CRM */}
                    <TimelineInteracciones
                      interacciones={clienteInteracciones}
                      onCrear={handleCrearInteraccion}
                      onActualizar={handleActualizarInteraccion}
                      onBorrar={handleBorrarInteraccion}
                    />

                    {/* Resumen de Presupuestos */}
                    <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6">
                      <h4 className="font-semibold text-gray-900 mb-3">Resumen de Actividad</h4>
                      <div className="grid grid-cols-3 gap-4 text-center mb-4">
                        <div>
                          <p className="text-2xl font-bold text-gray-900">{clienteEstadisticas.total_presupuestos}</p>
                          <p className="text-xs text-gray-600">Total Presupuestos</p>
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-green-600">{clienteEstadisticas.presupuestos_confirmados}</p>
                          <p className="text-xs text-gray-600">Confirmados</p>
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-red-600">{clienteEstadisticas.presupuestos_cancelados}</p>
                          <p className="text-xs text-gray-600">Cancelados</p>
                        </div>
                      </div>

                      {/* Listado de presupuestos del cliente */}
                      {clientePresupuestos.length > 0 && (
                        <div className="mt-4 border-t border-gray-200 pt-4">
                          <h5 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                            <FileText className="w-4 h-4" />
                            Presupuestos ({clientePresupuestos.length})
                          </h5>
                          <div className="max-h-80 overflow-y-auto divide-y divide-gray-100">
                            {[...clientePresupuestos]
                              .sort((a, b) => {
                                const fa = a.fecha_evento || '';
                                const fb = b.fecha_evento || '';
                                return fb.localeCompare(fa);
                              })
                              .map((p) => {
                                const numero = p.numero || p.numero_presupuesto_legacy || p.id?.slice(0, 8);
                                const esConfirmado = p.estado === 'confirmado' || p.estado === 'facturado';
                                const fecha = p.fecha_evento
                                  ? new Date(p.fecha_evento).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' })
                                  : '—';
                                const importe = Number(p.importe_total || 0);
                                return (
                                  <a
                                    key={p.id}
                                    href={`/presupuestos/${p.id}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center justify-between py-2 px-2 hover:bg-gray-50 rounded transition-colors group"
                                  >
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-2">
                                        <span className="font-semibold text-sm text-gray-900">#{numero}</span>
                                        {esConfirmado ? (
                                          <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700">
                                            {p.estado === 'facturado' ? 'Facturado' : 'Confirmado'}
                                          </span>
                                        ) : (
                                          <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 capitalize">
                                            {p.estado || 'borrador'}
                                          </span>
                                        )}
                                      </div>
                                      <p className="text-xs text-gray-500 truncate mt-0.5">
                                        {fecha}
                                        {p.lugar_evento ? ` · ${p.lugar_evento}` : ''}
                                      </p>
                                    </div>
                                    <div className="flex items-center gap-3 ml-3">
                                      <span className="text-sm font-semibold text-gray-900 whitespace-nowrap">
                                        €{importe.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                      </span>
                                      <ExternalLink className="w-4 h-4 text-gray-400 group-hover:text-duetto-primary" />
                                    </div>
                                  </a>
                                );
                              })}
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                )}

                {/* Comportamiento de Pago - CRM */}
                {clienteEstadisticasPago && clienteEstadisticasPago.tiene_historial && (
                  <div className="mb-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2"><DollarSign className="w-5 h-5 text-gray-600" /> Comportamiento de Pago</h3>

                    {/* Clasificación del Pagador */}
                    <div className={`rounded-lg p-4 mb-4 border-2 ${
                      clienteEstadisticasPago.clasificacion === 'excelente' ? 'bg-emerald-50 border-emerald-300' :
                      clienteEstadisticasPago.clasificacion === 'bueno' ? 'bg-green-50 border-green-300' :
                      clienteEstadisticasPago.clasificacion === 'normal' ? 'bg-yellow-50 border-yellow-300' :
                      clienteEstadisticasPago.clasificacion === 'lento' ? 'bg-orange-50 border-orange-300' :
                      clienteEstadisticasPago.clasificacion === 'muy_lento' ? 'bg-red-50 border-red-300' :
                      'bg-gray-50 border-gray-300'
                    }`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                            clienteEstadisticasPago.clasificacion === 'excelente' ? 'bg-emerald-200 text-emerald-700' :
                            clienteEstadisticasPago.clasificacion === 'bueno' ? 'bg-green-200 text-green-700' :
                            clienteEstadisticasPago.clasificacion === 'normal' ? 'bg-yellow-200 text-yellow-700' :
                            clienteEstadisticasPago.clasificacion === 'lento' ? 'bg-orange-200 text-orange-700' :
                            clienteEstadisticasPago.clasificacion === 'muy_lento' ? 'bg-red-200 text-red-700' :
                            'bg-gray-200 text-gray-700'
                          }`}>
                            {clienteEstadisticasPago.clasificacion === 'muy_lento'
                              ? <AlertTriangle className="w-6 h-6" />
                              : <Clock className="w-6 h-6" />}
                          </div>
                          <div>
                            <p className={`text-lg font-bold ${
                              clienteEstadisticasPago.clasificacion === 'excelente' ? 'text-emerald-800' :
                              clienteEstadisticasPago.clasificacion === 'bueno' ? 'text-green-800' :
                              clienteEstadisticasPago.clasificacion === 'normal' ? 'text-yellow-800' :
                              clienteEstadisticasPago.clasificacion === 'lento' ? 'text-orange-800' :
                              clienteEstadisticasPago.clasificacion === 'muy_lento' ? 'text-red-800' :
                              'text-gray-800'
                            }`}>
                              Pagador {
                                clienteEstadisticasPago.clasificacion === 'excelente' ? 'Excelente' :
                                clienteEstadisticasPago.clasificacion === 'bueno' ? 'Bueno' :
                                clienteEstadisticasPago.clasificacion === 'normal' ? 'Normal' :
                                clienteEstadisticasPago.clasificacion === 'lento' ? 'Lento' :
                                clienteEstadisticasPago.clasificacion === 'muy_lento' ? 'Muy Lento' : 'Sin datos'
                              }
                            </p>
                            <p className="text-sm text-gray-600">
                              {clienteEstadisticasPago.clasificacion === 'excelente' ? 'Paga en menos de 15 días' :
                               clienteEstadisticasPago.clasificacion === 'bueno' ? 'Paga en 15-30 días' :
                               clienteEstadisticasPago.clasificacion === 'normal' ? 'Paga en 1-2 meses' :
                               clienteEstadisticasPago.clasificacion === 'lento' ? 'Paga en 2-3 meses' :
                               clienteEstadisticasPago.clasificacion === 'muy_lento' ? 'Tarda más de 3 meses' : ''}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-3xl font-bold text-gray-900">{clienteEstadisticasPago.media_dias_pago?.toFixed(0) || 0}</p>
                          <p className="text-xs text-gray-600">días promedio</p>
                        </div>
                      </div>
                    </div>

                    {/* Detalles de Pago */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div className="bg-gray-50 rounded-lg p-3 text-center">
                        <p className="text-2xl font-bold text-gray-900">{clienteEstadisticasPago.total_pagos}</p>
                        <p className="text-xs text-gray-600">Total Pagos</p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-3 text-center">
                        <p className="text-2xl font-bold text-gray-900">€{(clienteEstadisticasPago.suma_importes || 0).toLocaleString('es-ES')}</p>
                        <p className="text-xs text-gray-600">Total Pagado</p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-3 text-center">
                        <p className="text-2xl font-bold text-green-600">{clienteEstadisticasPago.min_dias_pago ?? '-'}</p>
                        <p className="text-xs text-gray-600">Mín. días</p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-3 text-center">
                        <p className="text-2xl font-bold text-red-600">{clienteEstadisticasPago.max_dias_pago ?? '-'}</p>
                        <p className="text-xs text-gray-600">Máx. días</p>
                      </div>
                    </div>

                    {/* Último Pago */}
                    {clienteEstadisticasPago.ultimo_pago?.fecha && (
                      <div className="mt-3 bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <p className="text-sm text-blue-800">
                          <span className="font-semibold">Último pago:</span> {new Date(clienteEstadisticasPago.ultimo_pago.fecha).toLocaleDateString('es-ES')} 
                          {' · '}€{(clienteEstadisticasPago.ultimo_pago.importe || 0).toLocaleString('es-ES')}
                          {clienteEstadisticasPago.ultimo_pago.dias !== null && (
                            <span> · {clienteEstadisticasPago.ultimo_pago.dias} días desde factura</span>
                          )}
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Mensaje si no tiene historial de pago */}
                {clienteEstadisticasPago && !clienteEstadisticasPago.tiene_historial && (
                  <div className="mb-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2"><DollarSign className="w-5 h-5 text-gray-600" /> Comportamiento de Pago</h3>
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
                      <p className="text-gray-500">Este cliente aún no tiene historial de pagos registrados</p>
                    </div>
                  </div>
                )}

                {/* Personas de Contacto */}
                <div className="mt-6">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2"><Users className="w-5 h-5 text-gray-600" /> Personas de Contacto</h3>
                    <button
                      onClick={() => setShowAddContactoModal(true)}
                      className="px-3 py-1 bg-green-500 text-white rounded-lg hover:bg-green-600 transition flex items-center gap-1"
                    >
                      <Plus className="w-4 h-4" />
                      Añadir Contacto
                    </button>
                  </div>
                  
                  {/* Contacto Principal del Cliente */}
                  {(selectedCliente.persona_contacto || selectedCliente.email || selectedCliente.telefono) && (
                    <div className="mb-4">
                      <div className="flex justify-between items-center mb-2">
                        <h4 className="text-sm font-semibold text-gray-700">Contacto Principal</h4>
                        {!editandoContactoPrincipal && (
                          <button
                            onClick={handleOpenEditContactoPrincipal}
                            className="text-xs px-3 py-1 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition flex items-center gap-1"
                          >
                            <Edit className="w-3 h-3" />
                            Editar
                          </button>
                        )}
                      </div>
                      
                      {editandoContactoPrincipal ? (
                        <div className="bg-blue-50 border-2 border-blue-300 rounded-lg p-4 space-y-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Nombre de Contacto</label>
                            <input
                              type="text"
                              value={contactoPrincipalData.persona_contacto}
                              onChange={(e) => setContactoPrincipalData({...contactoPrincipalData, persona_contacto: e.target.value})}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                              placeholder="Ej: Beatriz Colorado"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Cargo</label>
                            <input
                              type="text"
                              value={contactoPrincipalData.cargo}
                              onChange={(e) => setContactoPrincipalData({...contactoPrincipalData, cargo: e.target.value})}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                              placeholder="Ej: Director General"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Email</label>
                            <input
                              type="email"
                              value={contactoPrincipalData.email}
                              onChange={(e) => setContactoPrincipalData({...contactoPrincipalData, email: e.target.value})}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                            />
                          </div>
                          <div className="grid grid-cols-3 gap-2">
                            <div className="col-span-2">
                              <label className="block text-xs font-medium text-gray-700 mb-1">Teléfono</label>
                              <input
                                type="text"
                                value={contactoPrincipalData.telefono}
                                onChange={(e) => setContactoPrincipalData({...contactoPrincipalData, telefono: e.target.value})}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">Extensión</label>
                              <input
                                type="text"
                                value={contactoPrincipalData.extension}
                                onChange={(e) => setContactoPrincipalData({...contactoPrincipalData, extension: e.target.value})}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                placeholder="Ej: 102"
                              />
                            </div>
                          </div>
                          <div className="flex gap-2 justify-end">
                            <button
                              onClick={() => setEditandoContactoPrincipal(false)}
                              className="px-3 py-1.5 text-xs border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                            >
                              Cancelar
                            </button>
                            <button
                              onClick={handleSaveContactoPrincipal}
                              className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                            >
                              Guardar
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="bg-blue-50 border-2 border-blue-300 rounded-lg p-4">
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center">
                              <User className="w-5 h-5 text-blue-600 mr-2" />
                              <div>
                                <p className="font-semibold text-blue-900">
                                  {selectedCliente.persona_contacto || 'Contacto Principal'}
                                </p>
                                <p className="text-xs text-blue-600">Contacto predeterminado</p>
                              </div>
                            </div>
                            <span className="bg-blue-600 text-white text-xs px-2 py-1 rounded">PRINCIPAL</span>
                          </div>
                          {selectedCliente.email && (
                            <div className="flex items-center text-sm text-gray-700 mb-1 gap-2">
                              <Mail className="w-4 h-4 text-blue-600 flex-shrink-0" />
                              <a
                                href={construirMailto(selectedCliente.email)}
                                className="hover:underline hover:text-blue-700"
                              >
                                {selectedCliente.email}
                              </a>
                              <a
                                href={construirMailto(selectedCliente.email)}
                                title="Enviar email"
                                className="ml-auto inline-flex items-center gap-1 px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                              >
                                <Send className="w-3 h-3" /> Email
                              </a>
                            </div>
                          )}
                          {selectedCliente.telefono && (
                            <div className="flex items-center text-sm text-gray-700 gap-2">
                              <Phone className="w-4 h-4 text-blue-600 flex-shrink-0" />
                              <a href={`tel:${selectedCliente.telefono}`} className="hover:underline hover:text-blue-700">
                                {selectedCliente.telefono}
                              </a>
                              {selectedCliente.extension_telefono && (
                                <span className="text-gray-500">ext. {selectedCliente.extension_telefono}</span>
                              )}
                              {construirWhatsapp(selectedCliente.telefono) && (
                                <a
                                  href={construirWhatsapp(selectedCliente.telefono)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  title="Abrir WhatsApp"
                                  className="ml-auto inline-flex items-center gap-1 px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700"
                                >
                                  <MessageCircle className="w-3 h-3" /> WhatsApp
                                </a>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Contactos Adicionales */}
                  {clientePersonasContacto && clientePersonasContacto.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-gray-700 mb-2">
                        Contactos Adicionales ({clientePersonasContacto.length})
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {clientePersonasContacto.map((persona) => (
                          <div key={persona.id}>
                            {editandoContactoAdicional === persona.id ? (
                              <div className="bg-gray-50 border border-gray-300 rounded-lg p-4 space-y-3">
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">Nombre *</label>
                                  <input
                                    type="text"
                                    value={contactoAdicionalData.nombre}
                                    onChange={(e) => setContactoAdicionalData({...contactoAdicionalData, nombre: e.target.value})}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">Cargo</label>
                                  <input
                                    type="text"
                                    value={contactoAdicionalData.cargo}
                                    onChange={(e) => setContactoAdicionalData({...contactoAdicionalData, cargo: e.target.value})}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">Email</label>
                                  <input
                                    type="email"
                                    value={contactoAdicionalData.email}
                                    onChange={(e) => setContactoAdicionalData({...contactoAdicionalData, email: e.target.value})}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                  />
                                </div>
                                <div className="grid grid-cols-3 gap-2">
                                  <div className="col-span-2">
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Teléfono</label>
                                    <input
                                      type="text"
                                      value={contactoAdicionalData.telefono}
                                      onChange={(e) => setContactoAdicionalData({...contactoAdicionalData, telefono: e.target.value})}
                                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Extensión</label>
                                    <input
                                      type="text"
                                      value={contactoAdicionalData.extension}
                                      onChange={(e) => setContactoAdicionalData({...contactoAdicionalData, extension: e.target.value})}
                                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                      placeholder="Ej: 102"
                                    />
                                  </div>
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">Notas</label>
                                  <textarea
                                    value={contactoAdicionalData.notas}
                                    onChange={(e) => setContactoAdicionalData({...contactoAdicionalData, notas: e.target.value})}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                    rows="2"
                                  />
                                </div>
                                <div className="flex gap-2 justify-end">
                                  <button
                                    onClick={() => setEditandoContactoAdicional(null)}
                                    className="px-3 py-1.5 text-xs border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                                  >
                                    Cancelar
                                  </button>
                                  <button
                                    onClick={handleSaveContactoAdicional}
                                    className="px-3 py-1.5 text-xs bg-green-600 text-white rounded-lg hover:bg-green-700"
                                  >
                                    Guardar
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                                <div className="flex items-start justify-between mb-2">
                                  <div className="flex items-center flex-1">
                                    <User className="w-5 h-5 text-gray-600 mr-2" />
                                    <div className="flex-1">
                                      <p className="font-semibold text-gray-900">{persona.nombre}</p>
                                      {persona.cargo && (
                                        <p className="text-xs text-gray-600">{persona.cargo}</p>
                                      )}
                                    </div>
                                  </div>
                                  <div className="flex gap-1">
                                    <button
                                      onClick={() => handleOpenEditContactoAdicional(persona)}
                                      className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                                      title="Editar"
                                    >
                                      <Edit className="w-4 h-4" />
                                    </button>
                                    <button
                                      onClick={() => handleDeleteContactoAdicional(persona.id)}
                                      className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                                      title="Eliminar"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  </div>
                                </div>
                                {persona.email && (
                                  <div className="flex items-center text-sm text-gray-600 mb-1">
                                    <Mail className="w-4 h-4 mr-2" />
                                    {persona.email}
                                  </div>
                                )}
                                {persona.telefono && (
                                  <div className="flex items-center text-sm text-gray-600">
                                    <Phone className="w-4 h-4 mr-2" />
                                    {persona.telefono}
                                    {persona.extension && (
                                      <span className="ml-2 text-gray-500">ext. {persona.extension}</span>
                                    )}
                                  </div>
                                )}
                                {persona.notas && (
                                  <p className="text-xs text-gray-500 mt-2 italic">
                                    {persona.notas}
                                  </p>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Mensaje si no hay contactos adicionales */}
                  {(!clientePersonasContacto || clientePersonasContacto.length === 0) && (
                    <div className="text-center py-6 text-gray-500 bg-gray-50 rounded-lg mt-2">
                      <User className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                      <p className="text-sm">No hay contactos adicionales</p>
                      <p className="text-xs mt-1">Se pueden añadir contactos adicionales al gestionar presupuestos</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Footer */}
            <div className="sticky bottom-0 bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => setShowDetallesModal(false)}
                className="px-6 py-2 bg-duetto-primary text-white rounded-lg hover:bg-opacity-90"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Modal para Añadir Contacto */}
      {showAddContactoModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="flex justify-between items-center p-6 border-b border-gray-200">
              <h3 className="text-xl font-semibold text-gray-900">Añadir Nuevo Contacto</h3>
              <button onClick={() => setShowAddContactoModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={nuevoContactoData.nombre}
                  onChange={(e) => setNuevoContactoData({...nuevoContactoData, nombre: e.target.value})}
                  placeholder="Nombre del contacto"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Cargo
                </label>
                <input
                  type="text"
                  value={nuevoContactoData.cargo}
                  onChange={(e) => setNuevoContactoData({...nuevoContactoData, cargo: e.target.value})}
                  placeholder="Ej: Director de Compras"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={nuevoContactoData.email}
                  onChange={(e) => setNuevoContactoData({...nuevoContactoData, email: e.target.value})}
                  placeholder="email@ejemplo.com"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                />
              </div>
              
              <div className="grid grid-cols-3 gap-2">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Teléfono
                  </label>
                  <input
                    type="tel"
                    value={nuevoContactoData.telefono}
                    onChange={(e) => setNuevoContactoData({...nuevoContactoData, telefono: e.target.value})}
                    placeholder="+34 600 000 000"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Extensión
                  </label>
                  <input
                    type="text"
                    value={nuevoContactoData.extension}
                    onChange={(e) => setNuevoContactoData({...nuevoContactoData, extension: e.target.value})}
                    placeholder="102"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notas
                </label>
                <textarea
                  value={nuevoContactoData.notas}
                  onChange={(e) => setNuevoContactoData({...nuevoContactoData, notas: e.target.value})}
                  placeholder="Notas adicionales..."
                  rows="3"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                />
              </div>
            </div>
            
            <div className="flex gap-3 p-6 border-t border-gray-200">
              <button
                onClick={() => {
                  setShowAddContactoModal(false);
                  setNuevoContactoData({
                    nombre: '',
                    cargo: '',
                    email: '',
                    telefono: '',
                    extension: '',
                    notas: ''
                  });
                }}
                className="flex-1 bg-gray-200 text-gray-700 py-2 rounded-lg hover:bg-gray-300"
              >
                Cancelar
              </button>
              <button
                onClick={handleAddContacto}
                className="flex-1 bg-green-600 text-white py-2 rounded-lg hover:bg-green-700"
              >
                Añadir Contacto
              </button>
            </div>
          </div>
        </div>
      )}

      {showSectoresModal && (
        <SectoresModal
          sectores={sectores}
          clientes={clientes}
          onClose={() => setShowSectoresModal(false)}
          onCrear={handleCrearSector}
          onEditar={handleEditarSector}
          onBorrar={handleBorrarSector}
        />
      )}
    </div>
  );
};

const ESTADO_FACTURA_LABEL = {
  emitida: 'Emitida',
  enviada: 'Enviada',
  vencida: 'Vencida',
  parcial: 'Pago parcial',
  parcialmente_pagada: 'Parcial',
  impagada: 'Impagada',
  borrador: 'Borrador',
  cobrada: 'Cobrada',
  pagada: 'Pagada',
  cancelada: 'Cancelada',
};

const FacturasClienteCard = ({ data, onVerFactura }) => {
  if (!data) return null;
  const {
    total_pendiente = 0,
    total_emitido = 0,
    pendientes_count = 0,
    count = 0,
    vencidas_count = 0,
    vencidas_importe = 0,
    facturas = [],
  } = data;

  if (!count) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6 flex items-center gap-3">
        <FileText className="w-5 h-5 text-gray-500 flex-shrink-0" />
        <div className="text-sm text-gray-700">
          <span className="font-semibold">Sin facturas emitidas.</span> Aún no se ha facturado a este cliente.
        </div>
      </div>
    );
  }

  const tonoResumen = vencidas_count > 0
    ? 'bg-red-50 border-red-300'
    : pendientes_count > 0
      ? 'bg-amber-50 border-amber-300'
      : 'bg-emerald-50 border-emerald-300';
  const totalColor = vencidas_count > 0
    ? 'text-red-700'
    : pendientes_count > 0
      ? 'text-amber-700'
      : 'text-emerald-700';

  return (
    <div className={`border rounded-lg p-4 mb-6 ${tonoResumen}`}>
      <div className="flex items-start justify-between mb-3 gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <FileText className={`w-5 h-5 ${totalColor}`} />
          <h4 className="font-semibold text-gray-900">
            Facturas emitidas <span className="text-gray-500 font-normal">({count})</span>
          </h4>
        </div>
        <div className="flex items-center gap-6 text-right">
          <div>
            <p className="text-xs text-gray-600">Pendiente</p>
            <p className={`text-xl font-bold ${totalColor}`}>{total_pendiente.toFixed(2)} €</p>
          </div>
          <div>
            <p className="text-xs text-gray-600">Total facturado</p>
            <p className="text-xl font-bold text-gray-800">{total_emitido.toFixed(2)} €</p>
          </div>
        </div>
      </div>
      {vencidas_count > 0 && (
        <div className="mb-3 px-3 py-2 bg-red-100 border border-red-200 rounded text-xs text-red-800">
          <strong>{vencidas_count}</strong> vencida{vencidas_count === 1 ? '' : 's'} — <strong>{vencidas_importe.toFixed(2)} €</strong> fuera de plazo
        </div>
      )}
      <div className="divide-y divide-gray-200 bg-white rounded border border-gray-200 max-h-80 overflow-y-auto">
        {facturas.map((f) => {
          const fechaEmis = f.fecha_emision ? new Date(f.fecha_emision).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—';
          const fechaVenc = f.fecha_vencimiento ? new Date(f.fecha_vencimiento).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' }) : null;
          const estadoLabel = ESTADO_FACTURA_LABEL[f.estado] || f.estado;
          const importeFmt = Number(f.importe_total || 0).toFixed(2);
          return (
            <div key={f.id} className="px-3 py-2 flex items-center justify-between gap-2 text-sm hover:bg-gray-50">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-gray-900">{f.numero_factura || f.id?.slice(0, 8)}</span>
                  {f.es_proforma ? (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-200 text-slate-700 font-semibold">PROFORMA</span>
                  ) : (
                    <>
                      {!f.pendiente && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 font-semibold">COBRADA</span>
                      )}
                      {f.pendiente && f.parcial && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-800 font-semibold">PARCIAL</span>
                      )}
                      {f.pendiente && !f.parcial && f.vencida && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-600 text-white font-semibold">VENCIDA</span>
                      )}
                      {f.pendiente && !f.parcial && !f.vencida && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-semibold">PENDIENTE</span>
                      )}
                    </>
                  )}
                  {!f.es_proforma && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 uppercase">
                      {estadoLabel}
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-0.5">
                  {f.es_proforma ? 'Emitida' : 'Emitida'} {fechaEmis}{fechaVenc ? ` · vence ${fechaVenc}` : ''}
                  {f.parcial && f.importe_pendiente ? ` · pendiente ${f.importe_pendiente.toFixed(2)} €` : ''}
                </p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <span className={`font-bold whitespace-nowrap ${f.vencida ? 'text-red-700' : 'text-gray-900'}`}>
                  {importeFmt} €
                </span>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); descargarFacturaPdf(f.id, f.numero_factura); }}
                  title="Descargar PDF"
                  className="p-1.5 rounded border border-gray-300 text-gray-600 hover:bg-blue-50 hover:text-blue-700 hover:border-blue-300"
                >
                  <Download className="w-4 h-4" />
                </button>
                {onVerFactura && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onVerFactura(f.id); }}
                    title="Ver factura en Finanzas"
                    className="p-1.5 rounded border border-gray-300 text-gray-600 hover:bg-gray-100"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const TIPO_INTERACCION_META = {
  nota: { label: 'Nota', icon: StickyNote, color: 'bg-gray-100 text-gray-700' },
  llamada: { label: 'Llamada', icon: PhoneCall, color: 'bg-blue-100 text-blue-700' },
  email: { label: 'Email', icon: Send, color: 'bg-purple-100 text-purple-700' },
  reunion: { label: 'Reunión', icon: Coffee, color: 'bg-amber-100 text-amber-800' },
  whatsapp: { label: 'WhatsApp', icon: MessageCircle, color: 'bg-green-100 text-green-700' },
  otro: { label: 'Otro', icon: MessageSquare, color: 'bg-slate-100 text-slate-700' },
};

// 'YYYY-MM-DDTHH:MM' en hora local para un <input type="datetime-local">
const fechaHoraLocalInput = (valor) => {
  const d = parsearFechaServidor(valor);
  if (!d) return '';
  const dos = (n) => String(n).padStart(2, '0');
  return `${fechaLocalISO(d)}T${dos(d.getHours())}:${dos(d.getMinutes())}`;
};

const formatFechaRelativa = (iso) => {
  if (!iso) return '';
  // U-102 (lote 7): sin zona, la hora es UTC (como la guarda el servidor)
  const d = parsearFechaServidor(iso);
  if (!d) return '';
  const diffMin = Math.round((Date.now() - d.getTime()) / 60000);
  if (diffMin < 1) return 'Ahora mismo';
  if (diffMin < 60) return `Hace ${diffMin} min`;
  const diffH = Math.round(diffMin / 60);
  if (diffH < 24) return `Hace ${diffH} h`;
  const diffD = Math.round(diffH / 24);
  if (diffD < 7) return `Hace ${diffD} d`;
  return d.toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });
};

const TimelineInteracciones = ({ interacciones, onCrear, onActualizar, onBorrar }) => {
  const [tipo, setTipo] = useState('nota');
  const [titulo, setTitulo] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [fecha, setFecha] = useState('');
  const [expandido, setExpandido] = useState(false);
  const [editandoId, setEditandoId] = useState(null);
  const [editData, setEditData] = useState(null);

  const reset = () => {
    setTipo('nota');
    setTitulo('');
    setDescripcion('');
    setFecha('');
    setExpandido(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!titulo.trim()) return;
    const payload = {
      tipo,
      titulo: titulo.trim(),
      descripcion: descripcion.trim() || null,
    };
    if (fecha) payload.fecha = new Date(fecha).toISOString();
    await onCrear(payload);
    reset();
  };

  const startEdit = (i) => {
    setEditandoId(i.id);
    setEditData({
      tipo: i.tipo || 'nota',
      titulo: i.titulo || '',
      descripcion: i.descripcion || '',
      // U-102 (lote 7): el campo datetime-local va en hora LOCAL (con la UTC de
      // toISOString la interacción se movía una o dos horas en cada edición)
      fecha: i.fecha ? fechaHoraLocalInput(i.fecha) : '',
    });
  };

  const saveEdit = async () => {
    const payload = {
      tipo: editData.tipo,
      titulo: editData.titulo.trim(),
      descripcion: editData.descripcion.trim() || null,
    };
    if (editData.fecha) payload.fecha = new Date(editData.fecha).toISOString();
    await onActualizar(editandoId, payload);
    setEditandoId(null);
    setEditData(null);
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6">
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-semibold text-gray-900 flex items-center gap-2">
          <MessageSquare className="w-4 h-4 text-duetto-primary" />
          Actividad y notas
          <span className="text-xs font-normal text-gray-500">({interacciones.length})</span>
        </h4>
      </div>

      {/* Formulario rápido */}
      <form onSubmit={handleSubmit} className="bg-gray-50 rounded-lg p-3 mb-4 space-y-2">
        <div className="flex items-center gap-2">
          <select
            value={tipo}
            onChange={(e) => setTipo(e.target.value)}
            className="px-2 py-1.5 border border-gray-300 rounded text-sm"
          >
            {Object.entries(TIPO_INTERACCION_META).map(([k, v]) => (
              <option key={k} value={k}>{v.label}</option>
            ))}
          </select>
          <input
            type="text"
            value={titulo}
            onChange={(e) => setTitulo(e.target.value)}
            onFocus={() => setExpandido(true)}
            placeholder="Resumen breve (ej. Llamada para confirmar fecha de evento)"
            className="flex-1 px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <button
            type="submit"
            disabled={!titulo.trim()}
            className="px-3 py-1.5 bg-duetto-primary text-white rounded text-sm disabled:opacity-50 hover:bg-opacity-90"
          >
            Añadir
          </button>
        </div>
        {expandido && (
          <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-2">
            <textarea
              value={descripcion}
              onChange={(e) => setDescripcion(e.target.value)}
              placeholder="Detalle (opcional)…"
              rows={2}
              className="px-3 py-2 border border-gray-300 rounded text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <div className="flex flex-col gap-1">
              <label className="text-xs text-gray-500">Fecha/hora</label>
              <input
                type="datetime-local"
                value={fecha}
                onChange={(e) => setFecha(e.target.value)}
                className="px-2 py-1.5 border border-gray-300 rounded text-sm"
              />
            </div>
          </div>
        )}
      </form>

      {/* Timeline */}
      {interacciones.length === 0 ? (
        <p className="text-sm text-gray-500 text-center py-6">
          Aún no hay interacciones registradas. Añade una nota arriba para empezar a construir el historial del cliente.
        </p>
      ) : (
        <ol className="relative border-l-2 border-gray-200 ml-3 space-y-4">
          {interacciones.map((i) => {
            const meta = TIPO_INTERACCION_META[i.tipo] || TIPO_INTERACCION_META.otro;
            const Icon = meta.icon;
            const isEditing = editandoId === i.id;
            return (
              <li key={i.id} className="ml-5 relative">
                <span className={`absolute -left-[30px] w-6 h-6 rounded-full flex items-center justify-center ${meta.color} ring-4 ring-white`}>
                  <Icon className="w-3.5 h-3.5" />
                </span>
                {isEditing ? (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-2">
                    <div className="flex items-center gap-2">
                      <select
                        value={editData.tipo}
                        onChange={(e) => setEditData({ ...editData, tipo: e.target.value })}
                        className="px-2 py-1 border border-gray-300 rounded text-xs"
                      >
                        {Object.entries(TIPO_INTERACCION_META).map(([k, v]) => (
                          <option key={k} value={k}>{v.label}</option>
                        ))}
                      </select>
                      <input
                        type="datetime-local"
                        value={editData.fecha}
                        onChange={(e) => setEditData({ ...editData, fecha: e.target.value })}
                        className="px-2 py-1 border border-gray-300 rounded text-xs"
                      />
                    </div>
                    <input
                      type="text"
                      value={editData.titulo}
                      onChange={(e) => setEditData({ ...editData, titulo: e.target.value })}
                      className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                    />
                    <textarea
                      value={editData.descripcion}
                      onChange={(e) => setEditData({ ...editData, descripcion: e.target.value })}
                      rows={2}
                      className="w-full px-2 py-1 border border-gray-300 rounded text-sm resize-none"
                    />
                    <div className="flex gap-2 justify-end">
                      <button
                        onClick={() => { setEditandoId(null); setEditData(null); }}
                        className="px-3 py-1 text-xs text-gray-600 hover:bg-gray-100 rounded"
                      >
                        Cancelar
                      </button>
                      <button
                        onClick={saveEdit}
                        disabled={!editData.titulo.trim()}
                        className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                      >
                        Guardar
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="bg-white border border-gray-200 rounded-lg p-3 group hover:border-gray-300 transition">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase ${meta.color}`}>
                            {meta.label}
                          </span>
                          <span className="text-xs text-gray-500">{formatFechaRelativa(i.fecha)}</span>
                          {i.autor_nombre && (
                            <span className="text-xs text-gray-400">· {i.autor_nombre}</span>
                          )}
                        </div>
                        <p className="text-sm font-medium text-gray-900 mt-1 break-words">{i.titulo}</p>
                        {i.descripcion && (
                          <p className="text-sm text-gray-600 mt-1 whitespace-pre-line break-words">{i.descripcion}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition">
                        <button
                          onClick={() => startEdit(i)}
                          className="p-1 text-gray-400 hover:text-blue-600"
                          title="Editar"
                        >
                          <Edit className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => onBorrar(i.id)}
                          className="p-1 text-gray-400 hover:text-red-600"
                          title="Eliminar"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
};

const TagsInput = ({ value = [], onChange, sugerencias = [] }) => {
  const [input, setInput] = useState('');
  const [focused, setFocused] = useState(false);

  const tags = Array.isArray(value) ? value : [];

  const addTag = (raw) => {
    const t = (raw || '').trim();
    if (!t) return;
    if (tags.some(x => x.toLowerCase() === t.toLowerCase())) {
      setInput('');
      return;
    }
    onChange([...tags, t]);
    setInput('');
  };

  const removeTag = (t) => {
    onChange(tags.filter(x => x !== t));
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag(input);
    } else if (e.key === 'Backspace' && !input && tags.length) {
      removeTag(tags[tags.length - 1]);
    }
  };

  const sugerenciasFiltradas = sugerencias
    .filter(s => s.tag.toLowerCase().includes(input.toLowerCase()))
    .filter(s => !tags.some(t => t.toLowerCase() === s.tag.toLowerCase()))
    .slice(0, 8);

  return (
    <div className="relative">
      <div className="flex flex-wrap items-center gap-1.5 px-3 py-2 border border-gray-300 rounded-lg focus-within:ring-2 focus-within:ring-blue-500 bg-white">
        {tags.map(t => (
          <span key={t} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
            {t}
            <button
              type="button"
              onClick={() => removeTag(t)}
              className="hover:text-indigo-900"
              aria-label={`Quitar ${t}`}
            >
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => setFocused(true)}
          onBlur={() => setTimeout(() => setFocused(false), 150)}
          placeholder={tags.length === 0 ? 'Escribe y pulsa Enter (ej. recurrente, corporate, bodas)…' : ''}
          className="flex-1 min-w-[140px] outline-none text-sm"
        />
      </div>
      {focused && input && sugerenciasFiltradas.length > 0 && (
        <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
          {sugerenciasFiltradas.map(s => (
            <button
              key={s.tag}
              type="button"
              onClick={() => addTag(s.tag)}
              className="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50 flex justify-between items-center"
            >
              <span>{s.tag}</span>
              <span className="text-xs text-gray-400">{s.count}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const SectoresModal = ({ sectores, clientes, onClose, onCrear, onEditar, onBorrar }) => {
  const [nuevoNombre, setNuevoNombre] = useState('');
  const [editandoId, setEditandoId] = useState(null);
  const [editandoNombre, setEditandoNombre] = useState('');

  const conteoPorSector = clientes.reduce((acc, c) => {
    if (c.sector_id) acc[c.sector_id] = (acc[c.sector_id] || 0) + 1;
    return acc;
  }, {});

  const handleSubmitNuevo = async (e) => {
    e.preventDefault();
    if (!nuevoNombre.trim()) return;
    await onCrear(nuevoNombre);
    setNuevoNombre('');
  };

  const handleGuardarEdicion = async (id) => {
    await onEditar(id, editandoNombre);
    setEditandoId(null);
    setEditandoNombre('');
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Tag className="w-5 h-5 text-purple-600" />
            Gestión de sectores
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 border-b">
          <form onSubmit={handleSubmitNuevo} className="flex gap-2">
            <input
              type="text"
              value={nuevoNombre}
              onChange={(e) => setNuevoNombre(e.target.value)}
              placeholder="Nuevo sector (ej. Hostelería, Corporate, Bodas)"
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button
              type="submit"
              disabled={!nuevoNombre.trim()}
              className="px-3 py-2 bg-duetto-primary text-white rounded-lg text-sm disabled:opacity-50 hover:bg-opacity-90 flex items-center gap-1"
            >
              <Plus className="w-4 h-4" /> Añadir
            </button>
          </form>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {sectores.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-8">
              No hay sectores creados todavía.
            </p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {sectores.map(s => (
                <li key={s.id} className="flex items-center gap-2 py-2">
                  {editandoId === s.id ? (
                    <>
                      <input
                        type="text"
                        value={editandoNombre}
                        onChange={(e) => setEditandoNombre(e.target.value)}
                        className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
                        autoFocus
                      />
                      <button
                        onClick={() => handleGuardarEdicion(s.id)}
                        className="px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700"
                      >
                        Guardar
                      </button>
                      <button
                        onClick={() => { setEditandoId(null); setEditandoNombre(''); }}
                        className="px-2 py-1 text-xs text-gray-600 hover:bg-gray-100 rounded"
                      >
                        Cancelar
                      </button>
                    </>
                  ) : (
                    <>
                      <span className="flex-1 text-sm text-gray-900">{s.nombre}</span>
                      <span className="text-xs text-gray-500">
                        {conteoPorSector[s.id] || 0} cliente(s)
                      </span>
                      <button
                        onClick={() => { setEditandoId(s.id); setEditandoNombre(s.nombre); }}
                        className="p-1 text-gray-500 hover:text-blue-600"
                        title="Editar"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => onBorrar(s.id, s.nombre)}
                        disabled={(conteoPorSector[s.id] || 0) > 0}
                        className="p-1 text-gray-500 hover:text-red-600 disabled:opacity-30 disabled:cursor-not-allowed"
                        title={(conteoPorSector[s.id] || 0) > 0 ? 'Hay clientes asignados' : 'Borrar'}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="p-4 border-t">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};

export default Clientes;